import { css, EditIcon, styled, Tooltip } from 'canoe';
import isEqual from 'lodash/isEqual';
import React from 'react';

import { ValueBox, ValueButton } from '@candu-ai/craft/ui';
import { useDefaultVariants } from '@candu-ai/styleguide/application-variants';
import { VariantNode } from '@candu-ai/styleguide/types';
import { useNavigation } from 'commons/hooks';
import { usePreparedVariants, VariantItem } from 'craft/hooks/usePreparedVariants';
import { STYLEGUIDE_PATH } from 'dashboard/pages/Styleguide/constants';

import DropdownBox from './DropdownBox';

const RowWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spaces.xs};
  `}
`;

const StyledDropdownBox = styled(DropdownBox)`
  flex-grow: 1;
  margin-bottom: 0;
  width: 158px;
`;

interface Props {
  name: string;
  componentName?: string;
  onSelectVariant: (variant: VariantNode) => void;
  conditions?: Record<string, string>;
}

export const Variants = React.memo(
  ({ componentName, onSelectVariant, conditions, name }: Props) => {
    const { data } = useDefaultVariants();
    const { variants: styleguideVariants } = data || {};

    const navigate = useNavigation();

    const variants = usePreparedVariants(componentName, styleguideVariants);

    const currentVariant = variants.find((v) => isEqual(v.value, name));
    const visibleVariants = variants.filter((v) => !v.isHidden);

    const onChangeVariant = (variantName: string) => {
      const variant = variants.find((v) => v.value === variantName);
      onSelectVariant({
        name: variantName,
        conditions: variant?.conditions || ({} as VariantItem['conditions']),
      });
    };

    return (
      <RowWrapper>
        <ValueBox label="Type" showOverflow>
          <StyledDropdownBox
            boxCss={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
            value={currentVariant?.name || 'Mixed Styles'}
            onChange={onChangeVariant}
            options={visibleVariants}
            trackingId="variant-select"
            footerAction={{
              label: '+ Create New Style',
              onClick: () => {
                navigate(`${STYLEGUIDE_PATH}/`, {
                  state: {
                    componentName,
                    conditions: { ...conditions, ...currentVariant?.conditions },
                    createNewStyle: true,
                  },
                });
              },
            }}
          />
        </ValueBox>
        <Tooltip content="Edit Style" placement="top">
          <ValueButton
            onClick={() => {
              navigate(`${STYLEGUIDE_PATH}/`, {
                state: {
                  componentName,
                  conditions: { ...conditions, ...currentVariant?.conditions },
                },
              });
            }}
          >
            <EditIcon color="OnyxGrayL30" />
          </ValueButton>
        </Tooltip>
      </RowWrapper>
    );
  },
);
